import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Stack,
  Divider,
  CircularProgress,
  Popover,
  List,
  ListItem,
  ListItemText,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  PictureAsPdf as PdfIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { downloadDocx } from '../utils/downloadDocx';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { configureCKEditorUpload } from './ckEditorUploadAdapter';
import jsPDF from 'jspdf';
import harvardCitationGuide from '../data/harvardCitationGuide';

// Optional debug telemetry: set REACT_APP_DEBUG_INGEST_URL to enable (e.g. http://127.0.0.1:7243/ingest/...)
const DEBUG_INGEST_URL = process.env.REACT_APP_DEBUG_INGEST_URL || '';

// Lazy load DecoupledEditor to avoid conflicts with ClassicEditor
let DecoupledEditor = null;
const loadDecoupledEditor = async () => {
  if (!DecoupledEditor) {
    const module = await import('@ckeditor/ckeditor5-build-decoupled-document');
    DecoupledEditor = module.default;
  }
  return DecoupledEditor;
};

const LogLevelIcon = ({ level }) => {
  switch (level) {
    case 'error':
      return <ErrorIcon color="error" fontSize="small" />;
    case 'warning':
      return <WarningIcon color="warning" fontSize="small" />;
    case 'info':
    default:
      return <InfoIcon color="info" fontSize="small" />;
  }
};

export default function LearningProgressModal({ 
  open, 
  onClose, 
  runId,
  pages = [],
  debugLogs = [],
  status = null,
  error = null,
  responseHtml = '',
  responseMeta = {},
  responseJson = null,
  responseStats = {},
  targetWordRange = { min: 5000, max: 6000 },
  onComplete,
  onCancel, // New prop for cancellation
  onSaved = null, // Called after build content is saved to BOK so parent can refresh saved list
  qualification = null,
  module = null,
  streamText = '', // New prop for accumulating stream text deltas
  progressSnapshots = [], // Progress snapshots for info popup
  taskCode = null, // Task/subtask code for saving build content to BOK on close
  doneMissingEndSections = false, // True when stream ended with incomplete References/Glossary/Abbreviations
  onEndSectionsGenerated = null // Callback after user successfully generates end sections (clears banner)
}) {
  const [expandedDebug, setExpandedDebug] = useState(false);
  const [generatingEndSections, setGeneratingEndSections] = useState(false);
  const [generateEndSectionsError, setGenerateEndSectionsError] = useState(null);
  // Use ref for large HTML string to reduce memory (only keep state for reactivity when needed)
  const streamContentRef = useRef(''); // Primary storage for stream content (reduces memory)
  const [streamContent, setStreamContent] = useState(''); // State for reactivity (derived from streamText)
  const [streamContentUpdateCounter, setStreamContentUpdateCounter] = useState(0); // Force React to detect updates
  const [editorInstance, setEditorInstance] = useState(null);
  const editorContainerRef = useRef(null);
  const editorMenuBarRef = useRef(null);
  const editorToolbarRef = useRef(null);
  const responseEditorContainerRef = useRef(null);
  const responseEditorMenuBarRef = useRef(null);
  const responseEditorToolbarRef = useRef(null);
  const logsEndRef = useRef(null);
  const lastStreamContentRef = useRef(''); // Track last content to force updates
  const lastEditorUpdateTimeRef = useRef(0); // Track last CKEditor update time for throttling
  const pendingEditorUpdateRef = useRef(null); // Track pending editor update
  const pendingContentRef = useRef(null); // Store pending content for batched updates
  const isUpdatingEditorRef = useRef(false); // Prevent re-entry into editor update logic
  const lastEditorContentRef = useRef(''); // Track what we last set in the editor to prevent duplicate updates
  const isUserEditRef = useRef(false); // Track if the current change is from user edit (not stream update)
  const isProgrammaticUpdateRef = useRef(false); // Track if we're updating editor programmatically (from setData)
  const [htmlPreviewOpen, setHtmlPreviewOpen] = useState(false);
  const [htmlPreviewContent, setHtmlPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [docxGenerating, setDocxGenerating] = useState(false);
  const [progressPopupOpen, setProgressPopupOpen] = useState(false);
  const [isEditorLayoutReady, setIsEditorLayoutReady] = useState(false);
  const [decoupledEditorLoaded, setDecoupledEditorLoaded] = useState(false);
  const [citationGuidePopoverAnchor, setCitationGuidePopoverAnchor] = useState(null);
  const [citationGuidePopoverOpen, setCitationGuidePopoverOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  
  // Track the longest content we've ever seen to prevent resets
  const maxContentLengthRef = useRef(0);
  const maxContentRef = useRef('');
  const lastUpdateTimestampRef = useRef(0); // Track when we last updated to force periodic updates
  const hasCompletedSaveRef = useRef(false); // Ensure completion save runs only once per run
  const lastInProgressSaveLengthRef = useRef(0); // For periodic save during generation
  const lastInProgressSaveTimeRef = useRef(0);
  const hasUnsavedEditsRef = useRef(false); // True after user edits; cleared on save; used for close warning
  
  // CRITICAL: Don't use data prop - it causes resets
  // Instead, we'll set content only via setData() in useEffect
  // This makes the editor truly uncontrolled

  // Get full CKEditor config - DecoupledEditor build includes all plugins
  // Don't spread defaultConfig to avoid module duplication - just configure what we need
  const ckEditorFullConfig = {
    toolbar: {
      items: [
        'undo',
        'redo',
        '|',
        'selectAll',
        '|',
        'heading',
        '|',
        'fontSize',
        'fontFamily',
        'fontColor',
        'fontBackgroundColor',
        '|',
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'link',
        'insertImage',
        'insertTable',
        'highlight',
        'blockQuote',
        '|',
        'alignment',
        '|',
        'bulletedList',
        'numberedList',
        'indent',
        'outdent',
        '|',
        'accessibilityHelp'
      ],
      shouldNotGroupWhenFull: false
    },
    fontFamily: {
      supportAllValues: true
    },
    fontSize: {
      options: [10, 12, 14, 'default', 18, 20, 22],
      supportAllValues: true
    },
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
        { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
        { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
        { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
      ]
    },
    image: {
      toolbar: [
        'toggleImageCaption',
        'imageTextAlternative',
        '|',
        'imageStyle:inline',
        'imageStyle:wrapText',
        'imageStyle:breakText',
        '|',
        'resizeImage'
      ]
    },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        toggleDownloadable: {
          mode: 'manual',
          label: 'Downloadable',
          attributes: {
            download: 'file'
          }
        }
      }
    },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true
      }
    },
    menuBar: {
      isVisible: true
    },
    placeholder: 'Type or paste your content here!',
    table: {
      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
    }
  };

  const scrollToBottom = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Function to clean JSON artifacts from HTML content
  const cleanJsonArtifacts = (html) => {
    if (!html || typeof html !== 'string') {
      return html;
    }
    
    // CRITICAL: Remove JSON artifacts that are mixed into HTML
    // These patterns appear when JSON data leaks into HTML content
    let cleaned = html;
    
    // ============= CRITICAL: Handle incremental streaming patterns =============
    // The LLM streams tool call arguments character-by-character, creating patterns like:
    // {"query":"{"query":"Har{"query":"Harvard{"query":"Harvard referencing...
    // {"url":"https{"url":"https://{"url":"https://scholar...
    
    // STEP 1: Remove entire blocks of incremental JSON tool call building
    // Match from first {"query": or {"url": to the end of that sequence (before HTML or end)
    cleaned = cleaned.replace(/\{"query"[^<]*(?=<|$)/g, '');
    cleaned = cleaned.replace(/\{"url"[^<]*(?=<|$)/g, '');
    cleaned = cleaned.replace(/^"query"/g, '');
    cleaned = cleaned.replace(/^"url"/g, '');
    cleaned = cleaned.replace(/\s+"query"/g, '');
    cleaned = cleaned.replace(/\s+"url"/g, '');
    
    // STEP 2: Handle nested incremental patterns (loop to collapse)
    let prevCleaned = '';
    let safetyCounter = 0;
    while (prevCleaned !== cleaned && safetyCounter < 10) {
      prevCleaned = cleaned;
      safetyCounter++;
      cleaned = cleaned
        .replace(/\{"query":"[^"]*\{"query":/g, '{"query":')
        .replace(/\{"url":"[^"]*\{"url":/g, '{"url":');
    }
    
    // STEP 3: Remove incomplete JSON anywhere in the string
    cleaned = cleaned.replace(/\{"query"[^}]*/g, '');
    cleaned = cleaned.replace(/\{"query"/g, '');
    cleaned = cleaned.replace(/\{"url"[^}]*/g, '');
    cleaned = cleaned.replace(/\{"url"/g, '');
    cleaned = cleaned.replace(/"query"/g, '');
    cleaned = cleaned.replace(/"url"/g, '');
    
    // ============= PATTERN B: Complete JSON removal =============
    
    // CRITICAL: Remove patterns like }{"run_id":"..."} that appear before HTML tags
    // This is the most common pattern - JSON objects mixed into HTML
    // Handle both complete and incomplete JSON objects
    cleaned = cleaned.replace(/\}\s*\{[^}]*"run_id"[^}]*\}\s*/g, '');
    cleaned = cleaned.replace(/\}\s*\{[^}]*"query"[^}]*\}\s*/g, '');
    cleaned = cleaned.replace(/\}\s*\{[^}]*"delta_html"[^}]*\}\s*/g, '');
    
    // Remove incomplete JSON objects that appear before HTML (e.g., }{"run_id":"..."<h3)
    // Match }{"run_id":"..." followed by < (HTML tag start) - this catches incomplete JSON
    cleaned = cleaned.replace(/\}\s*\{[^<]*"run_id"[^<]*\}(?=\s*<)/g, '');
    cleaned = cleaned.replace(/\}\s*\{[^<]*"query"[^<]*\}(?=\s*<)/g, '');
    
    // Remove standalone JSON objects at the start or middle of content
    cleaned = cleaned.replace(/^\s*\{[^}]*"run_id"[^}]*\}\s*/g, '');
    cleaned = cleaned.replace(/\s*\{[^}]*"run_id"[^}]*\}\s*/g, ' ');
    
    // Remove any remaining }{"run_id":"..." patterns (more aggressive)
    cleaned = cleaned.replace(/\}\s*\{[^}]*"run_id"[^}]*\}/g, '');
    cleaned = cleaned.replace(/\}\s*\{[^}]*"query"[^}]*\}/g, '');
    
    // Remove query/search JSON objects (complete objects)
    cleaned = cleaned.replace(/\{"query"\s*:\s*"[^"]*"\s*,\s*"numResults"\s*:\s*\d+[^}]*\}/g, '');
    cleaned = cleaned.replace(/\{"query"\s*:\s*"[^"]*"\s*,\s*"run_id"\s*:\s*"[^"]*"[^}]*\}/g, '');
    cleaned = cleaned.replace(/\{"query"\s*:\s*"[^"]*"\s*,\s*"topKChunks"\s*:\s*\d+[^}]*\}/g, '');
    
    // Remove URL JSON objects (with or without quotes around URL)
    cleaned = cleaned.replace(/\{"url"\s*:\s*"[^"]*"\}/g, '');
    cleaned = cleaned.replace(/\{"url"\s*:\s*[^}]*\}/g, '');
    
    // Remove run_id and progress state JSON objects
    cleaned = cleaned.replace(/\{"run_id"\s*:\s*"[^"]*"\s*,\s*"totalWordsSoFar"\s*:\s*\d+\}/g, '');
    cleaned = cleaned.replace(/\{"run_id"\s*:\s*"[^"]*"\}/g, '');
    // Remove progress/run_id fragments that leak mid-content (e.g. ,"totalWordsSoFar":269}}{"run_id":"run-...)
    cleaned = cleaned.replace(/,\s*"totalWordsSoFar"\s*:\s*\d+\s*}/g, '');
    cleaned = cleaned.replace(/\}\s*}\s*\{\s*"run_id"\s*:\s*"[^"]*"\s*(?:,\s*[^}]*)?\}/g, '');
    cleaned = cleaned.replace(/\}\s*\{\s*"run_id"\s*:\s*"[^"]*"\s*(?:,\s*[^}]*)?\}/g, '');
    cleaned = cleaned.replace(/\}\s*}\s*\{\s*"run_id"\s*:\s*"[^"]*"/g, '');
    cleaned = cleaned.replace(/\}\s*\{\s*"run_id"\s*:\s*"[^"]*"/g, '');
    
    // Remove progressStateSnapshot patterns (including nested objects)
    // Match "progressStateSnapshot":{...} with nested braces
    cleaned = cleaned.replace(/"progressStateSnapshot"\s*:\s*\{[^}]*"docId"[^}]*\}/g, '');
    cleaned = cleaned.replace(/"progressStateSnapshot"\s*:\s*\{[^}]*"docGoal"[^}]*\}/g, '');
    cleaned = cleaned.replace(/"progressStateSnapshot"\s*:\s*\{[^}]*"targetWordRange"[^}]*\}/g, '');
    // More aggressive: match any progressStateSnapshot with nested objects (up to 2000 chars)
    cleaned = cleaned.replace(/"progressStateSnapshot"\s*:\s*\{[^}]{0,2000}?\}/g, '');
    // Match incomplete progressStateSnapshot (cut off mid-object)
    cleaned = cleaned.replace(/"progressStateSnapshot"\s*:\s*\{[^<]*/g, '');
    
    // Remove current_word_count patterns (various formats)
    cleaned = cleaned.replace(/",\s*"current_word_count"\s*:\s*\d+\s*,\s*"progressStateSnapshot"\s*:\s*\{[^}]*\}/g, '');
    cleaned = cleaned.replace(/",\s*"current_word_count"\s*:\s*\d+/g, '');
    cleaned = cleaned.replace(/"\s*,\s*"current_word_count"\s*:\s*\d+/g, '');
    // Match incomplete patterns like ","current_word_count": (cut off)
    cleaned = cleaned.replace(/",\s*"current_word_count"\s*:\s*/g, '');
    cleaned = cleaned.replace(/"\s*,\s*"current_word_count"\s*:\s*/g, '');
    
    cleaned = cleaned.replace(/",\s*"delta_html"\s*:\s*"/g, '');
    cleaned = cleaned.replace(/",\s*"run_id"\s*:\s*"[^"]*"/g, '');
    cleaned = cleaned.replace(/\{"run_id"\s*:\s*"[^"]*",\s*"delta_html"\s*:\s*"/g, '');
    cleaned = cleaned.replace(/"\s*,\s*"progressStateSnapshot"\s*:\s*\{[^}]*\}/g, '');
    cleaned = cleaned.replace(/\{"docId"[^}]*\}/g, '');
    cleaned = cleaned.replace(/\{"targetWordRange"\s*:\s*\{[^}]*\}[^}]*\}/g, '');
    
    // Remove any remaining JSON objects with docId, docGoal, targetWordRange
    cleaned = cleaned.replace(/\{[^{}]*"docId"[^{}]*\}/g, '');
    cleaned = cleaned.replace(/\{[^{}]*"docGoal"[^{}]*\}/g, '');
    cleaned = cleaned.replace(/\{[^{}]*"targetWordRange"[^{}]*\}/g, '');
    
    // Remove any remaining JSON-like patterns with run_id (more aggressive)
    cleaned = cleaned.replace(/\{[^{}]*"run_id"[^{}]*\}/g, '');
    cleaned = cleaned.replace(/\}[^{}]*\{[^{}]*"run_id"[^{}]*\}/g, '');
    
    // Remove any remaining JSON-like patterns with query
    cleaned = cleaned.replace(/\{[^{}]*"query"[^{}]*\}/g, '');
    cleaned = cleaned.replace(/\}[^{}]*\{[^{}]*"query"[^{}]*\}/g, '');
    
    // Remove literal \n strings
    cleaned = cleaned.replace(/\\n/g, ' ');
    
    // Remove any remaining stray JSON fragments (like }{...} at start)
    // CRITICAL: Handle patterns like }}}...{"run_id":"..."<h3> (multiple closing braces)
    cleaned = cleaned.replace(/^\s*\}+[^{]*\{[^<]*"run_id"[^<]*\}/g, '');
    cleaned = cleaned.replace(/^\s*\}+[^{]*\{[^<]*/g, '');
    cleaned = cleaned.replace(/^\s*\{[^<]*"run_id"[^<]*\}/g, '');
    
    // CRITICAL: Remove patterns like }}}...{"run_id":"..." followed immediately by HTML tag
    // This catches incomplete JSON that's cut off right before HTML starts
    cleaned = cleaned.replace(/\}+[^{]*\{[^<]*"run_id"[^<]*\}(?=\s*<)/g, '');
    cleaned = cleaned.replace(/\}+[^{]*\{[^<]*"query"[^<]*\}(?=\s*<)/g, '');
    
    // ============= PATTERN C: Standalone metadata =============
    // Remove orphaned metadata that may remain after object removal
    cleaned = cleaned.replace(/"numResults"\s*:\s*\d+\s*,?/g, '');
    cleaned = cleaned.replace(/"topKChunks"\s*:\s*\d+\s*,?/g, '');
    cleaned = cleaned.replace(/"top"\s*,?/g, '');
    cleaned = cleaned.replace(/"num"\s*,?/g, '');
    
    // ============= PATTERN D: Cleanup =============
    // Remove orphaned braces/brackets at start/end
    cleaned = cleaned.replace(/^\s*[{}\[\],:]+\s*/g, '');
    cleaned = cleaned.replace(/\s*[{}\[\],:]+\s*$/g, '');
    
    // Remove sequences of just commas and whitespace
    cleaned = cleaned.replace(/^[\s,]+/g, '');
    cleaned = cleaned.replace(/[\s,]+$/g, '');
    
    // Collapse multiple spaces to single space
    cleaned = cleaned.replace(/\s{2,}/g, ' ');
    
    return cleaned.trim();
  };

  // Function to detect and filter citation guide content from HTML
  const filterCitationGuide = (html) => {
    if (!html || typeof html !== 'string') {
      return html;
    }
    
    // First clean JSON artifacts
    let cleanedHtml = cleanJsonArtifacts(html);

    // Patterns to detect citation guide content
    const citationGuidePatterns = [
      /Harvard is a commonly used method of referencing/i,
      /Which Harvard style\?/i,
      /Why reference your sources\?/i,
      /How do I use Harvard\?/i,
      /In-text citations are made like this/i,
      /Reference lists, at the end of your paper/i,
      /Style manual for authors, editors and printers/i
    ];

    // Check if any citation guide pattern exists
    const hasCitationGuide = citationGuidePatterns.some(pattern => pattern.test(cleanedHtml));

    if (!hasCitationGuide) {
      return cleanedHtml;
    }

    // Try to find the citation guide section - look for common markers
    // The citation guide typically appears at the end, after References section
    const referencesSectionRegex = /(<h[1-6][^>]*>.*?References.*?<\/h[1-6]>)/i;
    const referencesMatch = cleanedHtml.match(referencesSectionRegex);

    // Create a temporary DOM parser to extract text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanedHtml;

    // Find all text nodes and check for citation guide content
    const walker = document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let citationGuideStartNode = null;
    let citationGuideEndNode = null;
    let foundCitationGuide = false;

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      textNodes.push(node);
      const text = node.textContent || '';
      
      // Check if this text node contains citation guide patterns
      if (!foundCitationGuide && citationGuidePatterns.some(pattern => pattern.test(text))) {
        citationGuideStartNode = node;
        foundCitationGuide = true;
      }
      
      // If we found the start, look for the end (typically at the end of document or before next major section)
      if (foundCitationGuide && !citationGuideEndNode) {
        // Check if we've reached a new major heading (h1-h3) that's not part of the citation guide
        const parent = node.parentElement;
        if (parent) {
          const heading = parent.closest('h1, h2, h3');
          if (heading && heading !== parent && !heading.textContent.match(/citation|reference|harvard/i)) {
            // Found a new major section, end citation guide before this
            citationGuideEndNode = node.previousSibling || node;
            break;
          }
        }
      }
    }

    // If we found citation guide content, replace it with info icon
    if (foundCitationGuide && citationGuideStartNode) {
      // Find the parent element containing the citation guide
      let citationGuideElement = citationGuideStartNode.parentElement;
      
      // Walk up to find a block-level element (p, div, section, etc.)
      while (citationGuideElement && 
             !['P', 'DIV', 'SECTION', 'ARTICLE', 'BLOCKQUOTE'].includes(citationGuideElement.tagName) &&
             citationGuideElement !== tempDiv) {
        citationGuideElement = citationGuideElement.parentElement;
      }

      if (citationGuideElement && citationGuideElement !== tempDiv) {
        // Find where to insert the icon - after References section or at the end
        let insertPoint = null;
        
        if (referencesMatch) {
          // Find the References heading element
          const allElements = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
          for (let elem of allElements) {
            if (elem.textContent.match(/references/i)) {
              insertPoint = elem;
              break;
            }
          }
        }

        // If no References section, insert at the end of the document
        if (!insertPoint) {
          insertPoint = tempDiv.lastElementChild || tempDiv;
        }

        // Create info icon element
        const infoIconWrapper = document.createElement('span');
        infoIconWrapper.className = 'citation-guide-info-icon';
        infoIconWrapper.setAttribute('data-citation-guide-icon', 'true');
        infoIconWrapper.style.cssText = 'display: inline-block; margin-left: 8px; cursor: pointer; vertical-align: middle; color: #1976d2;';
        infoIconWrapper.innerHTML = 'ℹ️';
        infoIconWrapper.title = 'View Harvard Citation Guide';

        // Remove the citation guide content
        // Find all elements from citationGuideStartNode to the end or next major section
        let current = citationGuideStartNode;
        let elementsToRemove = [];
        
        while (current) {
          const parent = current.parentElement;
          if (parent && parent !== tempDiv) {
            // Check if this parent contains citation guide patterns
            const parentText = parent.textContent || '';
            if (citationGuidePatterns.some(pattern => pattern.test(parentText))) {
              elementsToRemove.push(parent);
              break;
            }
          }
          
          // Move to next sibling or parent's next sibling
          if (current.nextSibling) {
            current = current.nextSibling;
          } else if (current.parentElement && current.parentElement.nextSibling) {
            current = current.parentElement.nextSibling;
          } else {
            break;
          }
        }

        // Remove citation guide elements
        elementsToRemove.forEach(elem => {
          if (elem.parentElement) {
            elem.remove();
          }
        });

        // Insert info icon after References section or at end
        if (insertPoint && insertPoint.parentElement) {
          if (insertPoint.nextSibling) {
            insertPoint.parentElement.insertBefore(infoIconWrapper, insertPoint.nextSibling);
          } else {
            insertPoint.parentElement.appendChild(infoIconWrapper);
          }
        } else {
          tempDiv.appendChild(infoIconWrapper);
        }

        return tempDiv.innerHTML;
      }
    }

    // Fallback: Simple regex-based replacement if DOM parsing fails
    // Look for citation guide text and replace with info icon
    const citationGuideRegex = /(Harvard is a commonly used method[^]*?)(?=<h[1-6]|$)/i;
    const match = cleanedHtml.match(citationGuideRegex);
    
    if (match) {
      // Use a more CKEditor-friendly approach - create a span with a data attribute
      // that CKEditor should preserve, and use a visible icon
      const infoIconHtml = '<span data-citation-guide-icon="true" style="display: inline-block; margin: 0 8px; cursor: pointer; vertical-align: middle; color: #1976d2; font-size: 1.2em; font-weight: bold;" title="View Harvard Citation Guide" contenteditable="false">ℹ️</span>';
      
      // Try to insert after References section
      const referencesIndex = cleanedHtml.lastIndexOf(match[0]);
      if (referencesIndex > 0) {
        // Find References heading before this
        const beforeCitation = cleanedHtml.substring(0, referencesIndex);
        const referencesHeadingMatch = beforeCitation.match(/(<h[1-6][^>]*>.*?References.*?<\/h[1-6]>)/i);
        
        if (referencesHeadingMatch) {
          const insertIndex = referencesHeadingMatch.index + referencesHeadingMatch[0].length;
          // Insert icon right after References heading, then remove citation guide
          return cleanedHtml.substring(0, insertIndex) + infoIconHtml + cleanedHtml.substring(insertIndex, referencesIndex) + cleanedHtml.substring(referencesIndex + match[0].length);
        }
      }
      
      // If no References section, replace citation guide with icon at end
      return cleanedHtml.replace(citationGuideRegex, infoIconHtml);
    }

    return cleanedHtml;
  };

  useEffect(() => {
    if (expandedDebug) {
      setTimeout(() => scrollToBottom(logsEndRef), 100);
    }
  }, [debugLogs, expandedDebug]);

  useEffect(() => {
    setIsEditorLayoutReady(true);
    // Load DecoupledEditor asynchronously
    loadDecoupledEditor().then(() => {
      setDecoupledEditorLoaded(true);
    });
    return () => setIsEditorLayoutReady(false);
  }, []);

  // Handle clicks on citation guide info icon using event delegation
  useEffect(() => {
    if (!editorInstance) return;

    const handleCitationGuideClick = (event) => {
      // Check if click is on citation guide info icon
      const target = event.target;
      const iconElement = target?.closest?.('[data-citation-guide-icon="true"]') || 
                         (target?.getAttribute?.('data-citation-guide-icon') === 'true' ? target : null);
      
      if (iconElement) {
        event.preventDefault();
        event.stopPropagation();
        
        // Get the position of the icon for popover anchoring
        const rect = iconElement.getBoundingClientRect();
        const anchorElement = {
          getBoundingClientRect: () => rect,
          clientWidth: rect.width,
          clientHeight: rect.height
        };
        
        setCitationGuidePopoverAnchor(anchorElement);
        setCitationGuidePopoverOpen(true);
      }
    };

    // Listen for clicks in the editor - use capture phase to catch before CKEditor handles it
    const editorElement = editorInstance.editing?.view?.domElement;
    if (editorElement) {
      editorElement.addEventListener('click', handleCitationGuideClick, true);
      return () => {
        editorElement.removeEventListener('click', handleCitationGuideClick, true);
      };
    }
  }, [editorInstance]);

  // Memoize status flags to reduce re-computations
  // CRITICAL: isGenerating should NOT depend on streamContent to avoid circular dependencies
  // Use status directly instead, and check streamContent length only when needed
  const isComplete = useMemo(() => (status === 'done' || error) && status !== 'cancelled', [status, error]);
  const isGenerating = useMemo(() => {
    // Base isGenerating on status only to avoid circular dependency with streamContent updates
    return status === 'generating';
  }, [status]);

  const handleGenerateEndSections = useCallback(async () => {
    setGenerateEndSectionsError(null);
    setGeneratingEndSections(true);
    try {
      const content = editorInstance && typeof editorInstance.getData === 'function'
        ? editorInstance.getData()
        : (streamContentRef.current || streamContent || '');
      if (!content || content.trim().length === 0) {
        setGenerateEndSectionsError('No content to generate from.');
        return;
      }
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token && token !== 'undefined' && token.trim() !== '') headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch('/api/learning/generate-end-sections', {
        method: 'POST',
        headers,
        body: JSON.stringify({ html_content: content })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setGenerateEndSectionsError(data.message || 'Failed to generate end sections.');
        return;
      }
      if (data.html_to_append) {
        if (editorInstance && typeof editorInstance.setData === 'function') {
          editorInstance.setData(content + '\n' + data.html_to_append);
        } else {
          const newContent = content + '\n' + data.html_to_append;
          streamContentRef.current = newContent;
          setStreamContent(newContent);
        }
        onEndSectionsGenerated?.();
      }
    } catch (e) {
      setGenerateEndSectionsError(e.message || 'Request failed.');
    } finally {
      setGeneratingEndSections(false);
    }
  }, [editorInstance, streamContent, onEndSectionsGenerated]);
  
  // Reset completion-save guard and unsaved state when a new generation run starts
  useEffect(() => {
    if (status === 'generating') {
      hasCompletedSaveRef.current = false;
      lastInProgressSaveLengthRef.current = 0;
      lastInProgressSaveTimeRef.current = 0;
      hasUnsavedEditsRef.current = false;
    }
  }, [status]);

  // Save during generation (periodic) so content is retrievable even if closed early
  const IN_PROGRESS_SAVE_INTERVAL_MS = 20000; // Every 20 seconds
  const IN_PROGRESS_SAVE_MIN_GROWTH = 1500;   // Or when content grew by this many chars
  useEffect(() => {
    if (status !== 'generating' || !runId || !qualification?.id) return;
    const content = streamContentRef.current || '';
    if (!content || content.length < 500) return;
    const now = Date.now();
    const lastLen = lastInProgressSaveLengthRef.current;
    const lastTime = lastInProgressSaveTimeRef.current;
    const growth = content.length - lastLen;
    if (lastLen > 0 && growth < IN_PROGRESS_SAVE_MIN_GROWTH && now - lastTime < IN_PROGRESS_SAVE_INTERVAL_MS) return;
    lastInProgressSaveLengthRef.current = content.length;
    lastInProgressSaveTimeRef.current = now;
    const moduleName = module?.title || module?.description || responseJson?.document_title || `Module ${module?.code || '1'}`;
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    fetch('/api/learning/save-document', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        run_id: runId,
        html_content: content,
        title: moduleName,
        qualification_id: qualification.id
      })
    }).then((res) => {
      if (res.ok) console.log('[LearningProgressModal] In-progress save ok, length:', content.length);
    }).catch(() => {});
  }, [status, runId, qualification?.id, module, responseJson, streamContentUpdateCounter]);

  // Auto-save document when generation completes (to LearningDocument and to BOK) — run only once per completion
  useEffect(() => {
    if (!isComplete || !runId || !qualification?.id || !streamContentRef.current || streamContentRef.current.length === 0) return;
    if (hasCompletedSaveRef.current) return;
    hasCompletedSaveRef.current = true;
    const contentToSave = streamContentRef.current;
    const moduleName = module?.title || module?.description || responseJson?.document_title || `Module ${module?.code || '1'}`;
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const saveDocument = async () => {
      try {
        const saveResponse = await fetch('/api/learning/save-document', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            run_id: runId,
            html_content: contentToSave,
            title: moduleName,
            qualification_id: qualification.id
          })
        });
        if (saveResponse.ok) {
          console.log('[LearningProgressModal] ✅ Document auto-saved on completion');
          setSaveSuccess(true);
          hasUnsavedEditsRef.current = false;
        } else {
          const errorText = await saveResponse.text();
          console.error('[LearningProgressModal] ❌ Failed to auto-save document:', errorText);
          setSaveError('Failed to auto-save document');
        }
      } catch (error) {
        console.error('[LearningProgressModal] ❌ Error auto-saving document:', error);
        setSaveError('Error auto-saving document');
      }
    };

    const saveToBok = async () => {
      if (!taskCode || !qualification?.id || !contentToSave?.trim()) return;
      try {
        const res = await fetch(`/api/qualifications/${qualification.id}/learning-content/save-build-content`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            subtask_code: taskCode,
            title: moduleName,
            html_content: contentToSave
          })
        });
        if (res.ok && onSaved) onSaved(taskCode);
      } catch (_) {}
    };

    const run = async () => {
      await saveDocument();
      await saveToBok();
    };
    const saveTimeout = setTimeout(run, 1000);
    return () => clearTimeout(saveTimeout);
  }, [isComplete, runId, qualification?.id, module, responseJson, taskCode, onSaved]);

  // CRITICAL: Load existing content from database when modal opens with a runId
  // This ensures previously generated content is displayed and maintained
  useEffect(() => {
    if (runId && open && !streamContent && streamContentRef.current.length === 0) {
      // Only load if we don't have content yet and modal just opened
      const loadExistingContent = async () => {
        try {
          const token = localStorage.getItem('token');
          const headers = {
            'Content-Type': 'application/json'
          };
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
          
          const response = await fetch(`/api/learning/document/${runId}`, {
            method: 'GET',
            headers
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.document && data.document.html_content) {
              const existingContent = data.document.html_content;
              console.log('[LearningProgressModal] ✅ Loaded existing content from database:', {
                length: existingContent.length,
                runId
              });
              
              // Set content in ref and state
              streamContentRef.current = existingContent;
              lastStreamContentRef.current = existingContent;
              setStreamContent(existingContent);
              
              // Update editor if it's ready
              if (editorInstance) {
                editorInstance.setData(existingContent);
                maxContentRef.current = existingContent;
                maxContentLengthRef.current = existingContent.length;
              }
            }
          } else if (response.status !== 404) {
            // 404 is fine (no document yet), but log other errors
            console.warn('[LearningProgressModal] ⚠️ Failed to load existing content:', response.status);
          }
        } catch (error) {
          console.error('[LearningProgressModal] ❌ Error loading existing content:', error);
          // Don't block UI if loading fails
        }
      };
      
      loadExistingContent();
    }
  }, [runId, open, editorInstance]); // Load when modal opens with runId

  // Accumulate stream text deltas - streamText contains the full accumulated text from backend
  // CRITICAL: The backend sends FULL accumulated HTML in each debug event (fullText field)
  // We should ALWAYS use it to ensure the editor shows the complete document
  useEffect(() => {
    // #region agent log - Log every streamText prop change
    if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LearningProgressModal.jsx:695',message:'useEffect triggered for streamText',data:{streamTextLength:streamText?.length||0,streamTextType:typeof streamText,status,hasEditorInstance:!!editorInstance,prevRefLength:streamContentRef.current?.length||0},timestamp:Date.now(),sessionId:'debug-session',runId:runId,hypothesisId:'H3'})}).catch(()=>{});
    // #endregion
    
    // Always update if streamText is provided (even if empty string during generation)
    // BUT: Don't overwrite existing content with empty string if we already have content
    if (streamText !== undefined && streamText !== null) {
      const textLength = streamText.length;
      const isCurrentlyGenerating = status === 'generating';
      
      // CRITICAL: During generation, ALWAYS update with latest content from backend
      // The backend sends full accumulated HTML each time - we must trust it
      // The backend is the source of truth for accumulated content
      // Even if content appears shorter (due to cleaning), we should use it
      if (isCurrentlyGenerating && textLength > 0) {
        // During generation, always accept backend's accumulated HTML
        // Don't block updates based on length - backend knows best
        // The backend accumulates deltas correctly and sends full content
        // Filter citation guide content and replace with info icon
        const filteredContent = filterCitationGuide(streamText);
        const filteredLength = filteredContent.length;
        
        // #region agent log
        if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LearningProgressModal.jsx:708',message:'streamText prop changed during generation',data:{streamTextLength:textLength,filteredLength,prevLength:streamContentRef.current.length},timestamp:Date.now(),sessionId:'debug-session',runId:runId,hypothesisId:'E'})}).catch(()=>{});
        // #endregion
        
        // CRITICAL: During generation, ALWAYS update if we have content
        // The backend sends full accumulated HTML (all deltas), so we just use it
        // CRITICAL: Only update if new content is longer or equal to current content
        // This prevents shorter content from overwriting longer accumulated content
        const prevRefContent = streamContentRef.current;
        const prevLength = prevRefContent.length;
        
        // Backend sends full accumulated HTML; a 20%-growth rule hides References/Glossary/Abbreviations
        // on long documents (TaskAgentBuilder content_delta uses the same fix).
        const isFirstUpdate = prevLength === 0;
        const meetsFirstUpdateThreshold = isFirstUpdate && filteredLength > 0;
        const meetsSubsequentUpdateThreshold = !isFirstUpdate && filteredLength > prevLength;
        
        if (meetsFirstUpdateThreshold || meetsSubsequentUpdateThreshold) {
        // CRITICAL: Always update during generation - backend sends full accumulated HTML
        // The backend accumulates all deltas, so filteredContent is the complete document
        streamContentRef.current = filteredContent;
        lastStreamContentRef.current = filteredContent;
        } else if (filteredLength < prevLength) {
          // Received shorter content - preserve current longer content
          console.warn('[LearningProgressModal] ⚠️ Received shorter filteredContent during generation, preserving current content:', {
            receivedLength: filteredLength,
            currentLength: prevLength,
            difference: prevLength - filteredLength
          });
          // Don't update - preserve current longer content
          return; // Exit early to prevent updating with shorter content
        }
        
        // CRITICAL: Always update state to trigger useEffect
        // The backend accumulates all deltas, so filteredContent is the complete document
        // Force React to detect the change by always setting state, even if content appears similar
        // CRITICAL: Create a new string reference to force React to detect the change
        const contentToSet = String(filteredContent); // Force new string reference
        
        // CRITICAL: Update state directly (not functional) to ensure it updates immediately
        // Functional updates can be batched, which causes delays
        // We need immediate updates during generation
        const prevStateLength = streamContent?.length || 0;
        const newStateLength = contentToSet.length;
        
        // CRITICAL: Always update state during generation to ensure editor gets latest content
        setStreamContent(contentToSet);
        
        // #region agent log
        if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LearningProgressModal.jsx:742',message:'setStreamContent called',data:{contentLength:contentToSet.length,prevStateLength},timestamp:Date.now(),sessionId:'debug-session',runId:runId,hypothesisId:'E'})}).catch(()=>{});
        // #endregion
        
        // CRITICAL: Also update counter to force React to detect the change
        setStreamContentUpdateCounter(prev => prev + 1);
        
        // Reduced logging - only log significant changes (every 1000 chars or major deltas)
        if (Math.abs(newStateLength - prevStateLength) > 500 || newStateLength % 1000 < 100) {
          console.log('[LearningProgressModal] ✅ Stream content updated:', {
            prevLength,
            newLength: filteredLength,
            delta: filteredLength - prevLength,
            streamTextLength: streamText.length
          });
        }
        return; // Always return during generation
      }
      
      // Not generating - use normal logic
      const currentContentLength = streamContentRef.current.length;
      
      // CRITICAL: If we have existing content and new streamText is empty, preserve existing content
      // This prevents clearing content when stream stops or is cancelled
      if (textLength === 0 && currentContentLength > 0) {
        return; // Don't update - preserve existing content
      }
      
      // Filter citation guide content and replace with info icon
      const filteredContent = filterCitationGuide(streamText);
      const filteredLength = filteredContent.length;
      
      // Update if content changed
      if (filteredLength > 0 && filteredContent !== streamContentRef.current) {
        // Store in ref first (primary storage, reduces memory)
        streamContentRef.current = filteredContent;
        lastStreamContentRef.current = filteredContent;
        // Update state for reactivity (triggers CKEditor update)
        setStreamContent(filteredContent);
      }
    }
  }, [streamText, status]); // Use status instead of isGenerating to break circular dependency

  // Update editor with stream content as it arrives - update immediately on every change
  useEffect(() => {
    // Reduced logging - only log when editor is missing or significant changes
    
    if (!editorInstance) {
      // Editor not ready yet - the onReady callback will apply content when editor becomes available
      return;
    }
    
    const isCurrentlyGenerating = status === 'generating';
    
    // CRITICAL: During generation, skip ALL early exit checks
    // We need to update immediately to show new deltas
    if (!isCurrentlyGenerating) {
      // Prevent re-entry to avoid infinite loops (only when not generating)
      if (isUpdatingEditorRef.current) {
        console.log('[LearningProgressModal] ⏸️ Already updating (not generating), skipping');
        return;
      }
      
      // CRITICAL: Don't update editor if change came from user edit (prevents infinite loop)
      if (isUserEditRef.current) {
        console.log('[LearningProgressModal] ⏸️ User edit in progress, skipping');
        return;
      }
    }
    
    // CRITICAL: During generation, ALWAYS check for content in both state and ref
    // The state might not have updated yet, so check ref as fallback
    // This ensures we don't miss any content updates
    const contentFromState = streamContent;
    const contentFromRef = streamContentRef.current;
    const hasContentInState = contentFromState !== undefined && contentFromState !== null && contentFromState.length > 0;
    const hasContentInRef = contentFromRef !== undefined && contentFromRef !== null && contentFromRef.length > 0;
    
    // CRITICAL: During generation, ALWAYS prefer ref over state
    // Refs update immediately, state updates are batched by React
    // This ensures we always have the latest content during streaming
    let contentToUse = null;
    if (isCurrentlyGenerating) {
      // During generation, prioritize ref (immediate updates) over state (batched updates)
      if (hasContentInRef) {
        // Ref has content - use it (most up-to-date)
        contentToUse = contentFromRef;
      } else if (hasContentInState) {
        // Fallback to state if ref is empty
        contentToUse = contentFromState;
      }
    } else {
      // Not generating - use state only
      if (hasContentInState) {
        contentToUse = contentFromState;
      }
    }
    
    if (contentToUse) {
      // Reduced logging - only log significant updates
      try {
        // CRITICAL: During generation, ALWAYS update to maintain the stream
        // The backend sends full accumulated HTML each time, we must show it
        // Skip ALL checks during generation - just update with latest content
        if (isCurrentlyGenerating && contentToUse.length > 0) {
          // During generation, use the content we determined above
          const latestContent = contentToUse;
          
          if (latestContent && latestContent.length > 0) {
            // Get current editor content to compare (for logging only)
            const currentEditorContent = editorInstance.getData();
            const currentLength = currentEditorContent.length;
            const latestLength = latestContent.length;
            
            // CRITICAL: During generation, ALWAYS use latestContent from backend
            // The backend sends full accumulated HTML, so we must trust it
            // Don't use max content logic during generation - it blocks legitimate updates
            let contentToSet = latestContent;
            
            // Track max for reference only, but don't use it to block updates
            if (latestLength > maxContentLengthRef.current) {
              maxContentLengthRef.current = latestLength;
              maxContentRef.current = latestContent;
            }
            
            // CRITICAL: During generation, ALWAYS update with latest content
            // The backend sends full accumulated HTML (all deltas combined)
            // We replace the editor content with the full accumulated HTML
            // Don't check if it's different - just always update during generation
            // The backend accumulates all deltas, so latestContent is the complete document
            
            // CRITICAL: Always update during generation - don't check if different
            // This ensures the editor always shows the latest accumulated content
            // Log every update to track what's happening
            const finalLength = contentToSet.length;
            const contentChanged = contentToSet !== currentEditorContent;
            const lengthChanged = finalLength !== currentLength;
            
            // CRITICAL: During generation, ALWAYS update even if content appears the same
            // The backend sends full accumulated HTML, so we must trust it and update
            // Don't check if content is different - just always update during generation
            // Skip all other checks - just update immediately
            // Backend sends full accumulated HTML, we replace editor content with it
            // CRITICAL: Don't set isUpdatingEditorRef during generation - it blocks subsequent updates
            // We want continuous streaming, so don't block with this flag
            isProgrammaticUpdateRef.current = true;
            
            lastStreamContentRef.current = contentToSet;
            lastEditorContentRef.current = contentToSet;
            
            // CRITICAL: Replace editor content with full accumulated HTML
            // This ensures the editor shows the complete document with all deltas
            // The backend accumulates all deltas, so this is the entire document
            // CRITICAL: Always call setData during generation, even if content appears the same
            // The backend might send the same content multiple times, but we must update to ensure consistency
            // CRITICAL: Force update if it's been more than 2 seconds since last update (even if content appears same)
            // This ensures we don't miss updates due to React optimization
            const now = Date.now();
            const timeSinceLastUpdate = now - lastUpdateTimestampRef.current;
            const shouldForceUpdate = timeSinceLastUpdate > 2000; // Force update every 2 seconds during generation
            
            try {
              // #region agent log
              if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LearningProgressModal.jsx:910',message:'Updating editor content',data:{currentLength,latestLength:finalLength,contentChanged,lengthChanged,shouldForceUpdate,timeSinceLastUpdate:Date.now()-(lastEditorUpdateTimeRef.current||0)},timestamp:Date.now(),sessionId:'debug-session',runId:runId,hypothesisId:'E'})}).catch(()=>{});
              lastEditorUpdateTimeRef.current = Date.now();
              // #endregion
              
              // Reduced logging - only log significant updates (every 1000 chars or major changes)
              if (Math.abs(finalLength - currentLength) > 500 || finalLength % 1000 < 100) {
                console.log('[LearningProgressModal] ✅ Editor updated:', {
                  length: finalLength,
                  delta: finalLength - currentLength
                });
              }
              editorInstance.setData(contentToSet);
              lastUpdateTimestampRef.current = now; // Update timestamp after setData
                // Removed verbose logging - only log warnings/errors
                
                // Verify the update worked (only log significant mismatches)
                // CKEditor may add/remove formatting, so small differences are normal
                setTimeout(() => {
                  const verifyContent = editorInstance.getData();
                  const verifyLength = verifyContent.length;
                  const difference = Math.abs(verifyLength - finalLength);
                  // Only warn if difference is significant (>100 chars) and editor has less content
                  if (difference > 100 && verifyLength < finalLength) {
                    console.warn('[LearningProgressModal] ⚠️ Editor content length mismatch after setData:', {
                      expected: finalLength,
                      actual: verifyLength,
                      difference: finalLength - verifyLength
                    });
                    // If editor has significantly less content than expected, retry with max content
                    if (verifyLength < finalLength && maxContentRef.current && maxContentRef.current.length > verifyLength) {
                      console.warn('[LearningProgressModal] ⚠️ Retrying with max content to restore full document');
                      try {
                        editorInstance.setData(maxContentRef.current);
                      } catch (retryError) {
                        console.error('[LearningProgressModal] ❌ Retry failed:', retryError);
                      }
                    }
                  } else if (verifyLength === 0 && finalLength > 0) {
                    console.error('[LearningProgressModal] ❌ Editor was reset to empty! Retrying setData...');
                    // Retry setting the content with max content if available
                    const retryContent = maxContentRef.current && maxContentRef.current.length > 0 
                      ? maxContentRef.current 
                      : contentToSet;
                    setTimeout(() => {
                      try {
                        editorInstance.setData(retryContent);
                        // Retry successful - no log needed
                      } catch (retryError) {
                        console.error('[LearningProgressModal] ❌ Retry failed:', retryError);
                      }
                    }, 100);
                  }
                  // Content verified - no log needed for successful verification
                }, 100);
            } catch (error) {
              console.error('[LearningProgressModal] ❌ Error setting editor data:', error);
              isProgrammaticUpdateRef.current = false;
              // Don't set isUpdatingEditorRef during generation - it blocks subsequent updates
              if (!isCurrentlyGenerating) {
                isUpdatingEditorRef.current = false;
              }
              return;
            }
            
            // Reset flags after a short delay - but keep isUpdatingEditorRef false during generation
            // to allow subsequent updates to come through immediately
            setTimeout(() => {
              isProgrammaticUpdateRef.current = false;
              // CRITICAL: Reset isUpdatingEditorRef immediately during generation to allow next update
              // Don't block subsequent updates - we want continuous streaming
              if (isCurrentlyGenerating) {
                isUpdatingEditorRef.current = false;
              } else {
                // Only delay reset when not generating to prevent rapid updates
                setTimeout(() => {
                  isUpdatingEditorRef.current = false;
                }, 50);
              }
            }, 50);
            
            // Auto-scroll to bottom
            requestAnimationFrame(() => {
              const editorElement = editorInstance.editing?.view?.domElement;
              if (editorElement) {
                editorElement.scrollTop = editorElement.scrollHeight;
              }
            });
          } // closes if (latestContent && latestContent.length > 0)
        }
        return; // Always return during generation - skip "not generating" section
        
        // Not generating - use normal update logic with checks
        // Early exit: Check if this exact content was already set in the editor
        if (streamContent === lastEditorContentRef.current && streamContent.length > 0) {
          return;
        }
        
        // Early exit: Check if we're already processing this content
        if (pendingContentRef.current === streamContent && streamContent.length > 0) {
          return;
        }
        
        // Early exit: If we're currently updating or this is a user edit, skip
        if (isUpdatingEditorRef.current || isUserEditRef.current) {
          return;
        }
        
        const currentContent = editorInstance.getData();
        const currentLength = currentContent.length;
        const newLength = streamContent.length;
        
        // CRITICAL: If the editor content already matches what we're trying to set, skip update
        if (currentContent === streamContent && currentLength > 0) {
          lastStreamContentRef.current = streamContent;
          lastEditorContentRef.current = streamContent;
          return;
        }
        
        // Check if content changed
        const contentChanged = streamContent !== currentContent;
        const lengthChanged = newLength !== currentLength;
        
        // Update if content is different
        const shouldUpdate = newLength > 0 && (
          currentLength === 0 || // Editor is empty
          (contentChanged && streamContent !== pendingContentRef.current) || // Content changed
          (lengthChanged && streamContent !== pendingContentRef.current) // Length changed
        );
      
      if (shouldUpdate) {
        // Store pending content
        pendingContentRef.current = streamContent;
        
        // During generation, update immediately to maintain stream
        // Only throttle when not generating to reduce memory pressure
        const now = Date.now();
        const timeSinceLastUpdate = now - lastEditorUpdateTimeRef.current;
        const THROTTLE_MS = isCurrentlyGenerating ? 100 : 200; // Faster updates during generation
        
        // Clear any pending update
        if (pendingEditorUpdateRef.current) {
          cancelAnimationFrame(pendingEditorUpdateRef.current);
          pendingEditorUpdateRef.current = null;
        }
        
        const performUpdate = () => {
          if (!editorInstance) {
            isUpdatingEditorRef.current = false;
            pendingContentRef.current = null;
            pendingEditorUpdateRef.current = null;
            return;
          }
          
        // CRITICAL: Always get the latest content from ref first (most up-to-date)
        // The ref is updated immediately when new content arrives, so it's always current
        // During generation, always use the latest content to maintain the stream
        const latestContent = streamContentRef.current || streamContent;
        
        // Use latest content if available, otherwise fall back to pending
        // But always prefer the latest content to ensure we show the full document
        // CRITICAL: During generation, always use latestContent to maintain stream
        let contentToUpdate = null;
        if (status === 'generating') {
          // During generation, always use latest content from ref (full accumulated HTML from backend)
          contentToUpdate = latestContent && latestContent.length > 0 ? latestContent : null;
        } else {
          // Not generating, use latest or pending
          contentToUpdate = (latestContent && latestContent.length > 0) 
            ? latestContent 
            : (pendingContentRef.current && pendingContentRef.current.length > 0)
              ? pendingContentRef.current
              : null;
        }
          
          if (!contentToUpdate || contentToUpdate.length === 0) {
            isUpdatingEditorRef.current = false;
            pendingContentRef.current = null;
            pendingEditorUpdateRef.current = null;
            return;
          }
          
          const updateLength = contentToUpdate.length;
          
          // Get current editor content to compare
          const currentEditorContent = editorInstance.getData();
          
          // Double-check we haven't already set this exact content
          if (contentToUpdate === lastEditorContentRef.current && updateLength > 0) {
            // Content already set, but check if editor actually has it
            if (currentEditorContent === contentToUpdate) {
              pendingContentRef.current = null;
              pendingEditorUpdateRef.current = null;
              isUpdatingEditorRef.current = false;
              return;
            }
          }
          
          // CRITICAL: During generation, ALWAYS update to show latest full content from backend
          // The backend sends full accumulated HTML, so we must trust it and show it
          // Don't preserve editor content during generation - the backend has the complete document
          if (status === 'generating') {
            // During generation, always update - backend sends full accumulated HTML
            // Don't check length - just use the latest content
          } else if (currentEditorContent.length > updateLength && currentEditorContent.length > updateLength * 1.1) {
            // Not generating and current content is significantly longer - might be user edits, preserve it
            pendingContentRef.current = null;
            pendingEditorUpdateRef.current = null;
            isUpdatingEditorRef.current = false;
            return;
          }
          
          // Set flag to prevent re-entry
          isUpdatingEditorRef.current = true;
          
          // Update the ref to track this update
          lastStreamContentRef.current = contentToUpdate;
          lastEditorContentRef.current = contentToUpdate; // Track what we set in editor
          lastEditorUpdateTimeRef.current = Date.now();
          
          // Update editor content
          try {
            // Set flag to indicate this is a programmatic update (not user edit)
            isProgrammaticUpdateRef.current = true;
            editorInstance.setData(contentToUpdate);
            // Reset flag after a short delay to allow onChange to check it
            setTimeout(() => {
              isProgrammaticUpdateRef.current = false;
            }, 50);
          } catch (error) {
            isProgrammaticUpdateRef.current = false; // Reset on error
          }
          
          // Clear pending content and reset flag after a short delay
          pendingContentRef.current = null;
          pendingEditorUpdateRef.current = null;
          
          // Reset flag after update completes (allow time for any events to settle)
          setTimeout(() => {
            isUpdatingEditorRef.current = false;
          }, 100);
          
          // Auto-scroll to bottom during generation
          if (status === 'generating' && updateLength > 0) {
            requestAnimationFrame(() => {
              const editorElement = editorInstance.editing?.view?.domElement;
              if (editorElement) {
                editorElement.scrollTop = editorElement.scrollHeight;
              }
            });
          }
        };
        
        // CRITICAL: During generation, always update immediately to maintain stream
        // The backend sends full accumulated HTML, we must show it without delay
        if (isCurrentlyGenerating || timeSinceLastUpdate >= THROTTLE_MS) {
          // Update immediately during generation or if enough time has passed
          performUpdate();
        } else {
          // Schedule update after throttle period, but check for latest content when it runs
          const delay = THROTTLE_MS - timeSinceLastUpdate;
          pendingEditorUpdateRef.current = requestAnimationFrame(() => {
            setTimeout(() => {
              // Before performing update, check if there's newer content
              const latestContent = streamContentRef.current || streamContent;
              if (latestContent && latestContent.length > 0) {
                // Update pending content to latest if it's newer/longer
                if (!pendingContentRef.current || 
                    latestContent.length > pendingContentRef.current.length ||
                    latestContent !== pendingContentRef.current) {
                  pendingContentRef.current = latestContent;
                }
              }
              performUpdate();
            }, delay);
          });
        }
      }
    } catch (error) {
      console.error('[LearningProgressModal] Error in editor update:', error);
      isUpdatingEditorRef.current = false; // Reset flag on error
    }
    } else {
      // No streamContent but we might have existing content in editor - preserve it
      try {
        const currentContent = editorInstance.getData();
        if (currentContent && currentContent.length > 0) {

        }
      } catch (error) {

      }
    }
  }, [streamContent, streamContentUpdateCounter, editorInstance, status]); // CRITICAL: Include counter to force updates
  
  // Cleanup: Cancel pending editor updates on unmount
  useEffect(() => {
    return () => {
      if (pendingEditorUpdateRef.current) {
        cancelAnimationFrame(pendingEditorUpdateRef.current);
        pendingEditorUpdateRef.current = null;
      }
      pendingContentRef.current = null;
      isUpdatingEditorRef.current = false; // Reset flag on unmount
    };
  }, [editorInstance, streamContent, status]); // CRITICAL: Include editorInstance so updates run when editor becomes ready

  // Note: We no longer build consolidated content from pages - we use streamContent directly
  // This useEffect is kept for backward compatibility but is effectively disabled
  useEffect(() => {
    if (false && pages && pages.length > 0 && isComplete) {
      // Sort pages by page_number, but also ensure we have valid page numbers
      const sortedPages = [...pages]
        .map((p, idx) => ({
          ...p,
          // Ensure page_number is valid, otherwise use index + 1
          _sortKey: (p.page_number !== undefined && p.page_number !== null && !isNaN(Number(p.page_number)) && Number(p.page_number) > 0) 
            ? Number(p.page_number) 
            : (idx + 1)
        }))
        .sort((a, b) => a._sortKey - b._sortKey);
      const consolidatedParts = sortedPages.map((page, index) => {
        // Get the full HTML content - support both html and html_content field names
        // Try html first (from events), then html_content (from database), then empty string
        const pageHtml = (page.html || page.html_content || '').trim();
        
        // Determine actual page number - use the _sortKey we calculated, or validate page_number
        let actualPageNumber = page._sortKey || (index + 1);
        if (!actualPageNumber || actualPageNumber < 1) {
          // Fallback validation
          if (page.page_number !== undefined && page.page_number !== null) {
            const pageNum = Number(page.page_number);
            if (!isNaN(pageNum) && pageNum > 0) {
              actualPageNumber = pageNum;
            } else {
              actualPageNumber = index + 1;
            }
          } else {
            actualPageNumber = index + 1;
          }
        }
        
        // Detailed logging for debugging - especially for first 2 pages
        if (index < 2) {
          // Debug info for first 2 pages
        }
        
        // Log if content seems incomplete or missing
        if (!pageHtml || pageHtml.length === 0) {
          // Return a placeholder if content is missing
          return `<div style="margin-top: 30px; padding: 20px; border: 2px dashed #ff9800; background-color: #fff3e0;">
            <h2 style="color: #0b4f79; margin-bottom: 10px;">Page ${actualPageNumber}${page.title ? `: ${page.title}` : ''}</h2>
            <p style="color: #f57c00; font-style: italic;">⚠️ Content for this page is missing or not yet loaded.</p>
            <p style="color: #666; font-size: 0.9em;">Debug: html=${!!page.html}, html_content=${!!page.html_content}</p>
          </div>`;
        }
        
        if (pageHtml.length < 100) {
          // Page HTML is very short
        }
        
        // Only add title if page doesn't already start with a heading
        const hasHeading = /^\s*<h[1-6][^>]*>/i.test(pageHtml);
        
        let pageTitle = '';
        if (!hasHeading && page.title) {
          pageTitle = `<h2 style="margin-top: 20px; margin-bottom: 15px; color: #0b4f79;">${page.title}</h2>`;
        } else if (!hasHeading) {
          pageTitle = `<h2 style="margin-top: 20px; margin-bottom: 15px; color: #0b4f79;">Page ${actualPageNumber}</h2>`;
        }
        
        // Add spacing between pages (except for the first one)
        const pageSpacing = index > 0 ? '<div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0;"></div>' : '';
        
        // CRITICAL: Ensure we always return the HTML content, even if it seems empty
        // Sometimes HTML might be whitespace-only, so check length after trim
        const finalContent = pageHtml.length > 0 ? pageHtml : '';
        
        // Log if we're about to return empty content
        if (!finalContent && index < 2) {
          // Empty content detected for first 2 pages
        }
        
        // Return the full page content - ensure we always include the HTML
        const pageContent = `${pageSpacing}${pageTitle}${finalContent}`;
        
        // Log each page's contribution to consolidation (for first 3 pages)
        if (index < 3) {
          // Debug info for first 3 pages
        }
        
        return pageContent;
      });
      
      let consolidated = consolidatedParts.join('\n');
      
      // Remove problematic images from consolidated content (Azure blob URLs that return 403)
      // This prevents 403 errors when displaying in CKEditor
      const problematicDomains = ['cdn.bankinghub.eu', 'bankinghub.eu'];
      consolidated = consolidated.replace(
        /<img[^>]*src=["']([^"']*)["'][^>]*>/gi,
        (match, url) => {
          if (!url) return '';
          
          // Remove problematic domains
          if (problematicDomains.some(domain => url.includes(domain))) {

            return '';
          }
          
          // Remove Azure blob storage URLs (they often return 403)
          if (url.includes('blob.core.windows.net') || url.includes('oaidalleapiprodscus')) {
            // Check for expiration date
            const expirationMatch = url.match(/[?&]se=(\d{4}-\d{2}-\d{2}T\d{2}%3A\d{2}%3A\d{2}Z)/);
            if (expirationMatch) {
              try {
                const expirationDate = new Date(decodeURIComponent(expirationMatch[1]));
                const now = new Date();
                if (expirationDate < now) {

                  return '';
                }
              } catch (e) {
                // If we can't parse, remove it

                return '';
              }
            } else {
              // No expiration date or potentially expired - remove to prevent 403 errors

              return '';
            }
          }
          
          return match;
        }
      );
      
      // Detailed logging for consolidation
       
      // Validate consolidation
      if (consolidated.length === 0 && pages.length > 0) {
        // Consolidated content is empty despite having pages
      }
      
      // Disabled - using streamContent instead
      // setConsolidatedContent(consolidated);
    } else {
      // Disabled - using streamContent instead
      // setConsolidatedContent('');
    }
  }, [pages]);

  // Pages prop is no longer used - content comes from streamText/streamContent

  // Memoize target word range calculations
  const safeTargetWordRange = useMemo(() => {
    return targetWordRange && typeof targetWordRange === 'object' && targetWordRange.min !== undefined && targetWordRange.max !== undefined
      ? targetWordRange 
      : { min: 9000, max: 11000 };
  }, [targetWordRange]);
  
  const targetWordMin = useMemo(() => safeTargetWordRange.min || 9000, [safeTargetWordRange.min]);
  const targetWordMax = useMemo(() => safeTargetWordRange.max || 11000, [safeTargetWordRange.max]);

  const generationNotes = responseJson?.generation_notes || {};
  // Use live word count from deltas if available; fallback to word count from stream content so it updates live
  const computedTotalWords = useMemo(() => {
    const fromMeta = responseMeta?.totalWords ?? responseStats?.totalWords ?? responseJson?.total_words;
    if (typeof fromMeta === 'number' && fromMeta > 0) return fromMeta;
    const content = streamContentRef.current || streamContent || '';
    if (!content || content.length === 0) return 0;
    const text = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return text ? text.split(/\s+/).filter(Boolean).length : 0;
  }, [responseMeta?.totalWords, responseStats?.totalWords, responseJson?.total_words, streamContent]);
  
  // Memoize content length calculation
  const streamContentLength = useMemo(() => {
    return streamContentRef.current?.length || streamContent?.length || 0;
  }, [streamContent]);
  
  // Log word count updates for debugging
  useEffect(() => {
  }, [responseMeta?.totalWords, responseStats?.totalWords, responseJson?.total_words, computedTotalWords, status]);
  
  // Memoize final stats to avoid recreating object on every render
  const finalStats = useMemo(() => {
    const estimatedReadingTime = responseStats.estimatedReadingTimeMin ?? 0;
    return {
      totalWords: computedTotalWords,
      estimatedReadingTime
    };
  }, [computedTotalWords, responseStats.estimatedReadingTimeMin]);
  // Memoize renderSourcesList to avoid recreating function on every render
  const renderSourcesList = useCallback((sources = []) => (
    <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 1 }}>
      {sources.map((source, index) => (
        <li key={`${source}-${index}`}>
          <Typography variant="body2">{source}</Typography>
        </li>
      ))}
    </Box>
  ), []);

  // Memoize handler functions to prevent unnecessary re-renders
  const handleClose = useCallback(async () => {
    // If preview modal is open, close it first; next click will close main modal
    if (htmlPreviewOpen) {
      setHtmlPreviewOpen(false);
      return;
    }
    // Warn if user has unsaved edits and confirm before closing
    if (hasUnsavedEditsRef.current && !window.confirm('You have unsaved changes. Close anyway?')) {
      return;
    }
    // When generating: cancel the stream then close so close button always works
    if (status === 'generating' && onCancel) {
      onCancel();
    }
    // Do not persist a mid-generation snapshot — that shrinks a completed document.
    const contentToSave = (editorInstance && typeof editorInstance.getData === 'function')
      ? editorInstance.getData()
      : (streamContentRef.current || '');
    if (
      status !== 'generating' &&
      taskCode &&
      qualification?.id &&
      contentToSave &&
      contentToSave.trim().length > 0
    ) {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token && token !== 'undefined' && token.trim() !== '') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      try {
        const res = await fetch(`/api/qualifications/${qualification.id}/learning-content/save-build-content`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            subtask_code: taskCode,
            title: module?.title || module?.description || taskCode,
            html_content: contentToSave
          })
        });
        if (res.ok && onSaved) {
          onSaved(taskCode);
        } else if (res.status === 400) {
          const data = await res.json().catch(() => ({}));
          if (data.code === 'MISSING_END_SECTIONS') {
            alert(data.message || 'Document must include References, Glossary, and Abbreviations before saving. Continue generation to add these sections.');
            return;
          }
        }
      } catch (_) {
        // ignore
      }
    }
    onClose();
    if (onComplete && isComplete) {
      onComplete(pages);
    }
  }, [htmlPreviewOpen, status, onClose, onComplete, onSaved, onCancel, isComplete, pages, taskCode, qualification?.id, module, editorInstance]);

  const handlePreviewHTML = useCallback(async (e) => {
    // Prevent any event propagation that might trigger modal close
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      setPreviewLoading(true);
      
      // Get content from CKEditor if available, otherwise use stream content
      // Use ref for content to avoid memory issues, fallback to state
      const contentToConvert = editorInstance ? editorInstance.getData() : (streamContentRef.current || streamContent);
      
      if (!contentToConvert) {
        alert('No content available to preview');
        setPreviewLoading(false);
        return;
      }

      // Extract programme name and module name
      const programmeName = qualification?.name || responseJson?.qualification_or_module_code || 'LEARNING PROGRAMME';
      const moduleName = module?.title || module?.description || responseJson?.document_title || `Module ${module?.code || '1'}`;

      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Get templated HTML preview
      const response = await fetch('/api/pdf/preview-html', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          programmeName: programmeName.toUpperCase(),
          moduleName: moduleName,
          contentHtml: contentToConvert
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to generate preview: ${response.statusText}`);
      }

      const htmlContent = await response.text();
      setHtmlPreviewContent(htmlContent);
      setHtmlPreviewOpen(true);
      setPreviewLoading(false);
    } catch (error) {

      alert(`Failed to generate preview: ${error.message}`);
      setPreviewLoading(false);
    }
  }, [editorInstance, streamContentRef, streamContent, qualification, module, responseJson]);

  const handleGeneratePDF = useCallback(async () => {
    try {
      setPdfGenerating(true);

      
      // Get content from CKEditor if available, otherwise use stream content
      // Use ref for content to avoid memory issues, fallback to state
      const contentToConvert = editorInstance ? editorInstance.getData() : (streamContentRef.current || streamContent);
      
      if (!contentToConvert || contentToConvert.trim().length === 0) {
        alert('No content available to generate PDF');
        setPdfGenerating(false);
        return;
      }



      // Extract programme name and module name
      const programmeName = qualification?.name || responseJson?.qualification_or_module_code || 'LEARNING PROGRAMME';
      const moduleName = module?.title || module?.description || responseJson?.document_title || `Module ${module?.code || '1'}`;

      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Save document to database first (if runId available)
      if (runId) {
        try {
          const saveResponse = await fetch('/api/learning/save-document', {
            method: 'POST',
            headers,
            body: JSON.stringify({
              run_id: runId,
              html_content: contentToConvert,
              title: moduleName,
              qualification_id: qualification?.id
            })
          });
          if (saveResponse.ok) {

          }
        } catch (saveError) {

          // Continue with PDF generation even if save fails
        }
      }
      

      // Generate PDF
      const response = await fetch('/api/pdf/preview', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          programmeName: programmeName.toUpperCase(),
          moduleName: moduleName,
          contentHtml: contentToConvert
        })
      });



      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText || `Failed to generate PDF: ${response.statusText}` };
        }
        throw new Error(errorData.message || errorData.error || `Failed to generate PDF: ${response.statusText}`);
      }


      const blob = await response.blob();
      
      // Verify blob is valid
      if (!blob || blob.size === 0) {
        throw new Error('PDF generation returned empty file');
      }
      


      
      // Create object URL and open in new window
      const url = window.URL.createObjectURL(blob);
      const pdfWindow = window.open(url, '_blank', 'width=1200,height=800');
      
      if (!pdfWindow) {
        // If popup blocked, fall back to download

        const a = document.createElement('a');
        a.href = url;
        a.download = `${programmeName.replace(/[^a-z0-9]/gi, '_')}-${moduleName.replace(/[^a-z0-9]/gi, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);
      } else {

        // Clean up URL when window is closed
        pdfWindow.addEventListener('beforeunload', () => {
          window.URL.revokeObjectURL(url);
        });
        // Also set up cleanup on window focus loss (user might close it)
        const cleanup = () => {
          setTimeout(() => {
            if (pdfWindow.closed) {
              window.URL.revokeObjectURL(url);
            }
          }, 1000);
        };
        window.addEventListener('blur', cleanup);
      }
      
      setPdfGenerating(false);
    } catch (error) {


      alert(`Failed to generate PDF: ${error.message}`);
      setPdfGenerating(false);
    }
  }, [editorInstance, streamContentRef, streamContent, qualification, module, responseJson]);

  const handleDownloadWord = useCallback(async () => {
    try {
      setDocxGenerating(true);
      const contentToConvert = editorInstance ? editorInstance.getData() : (streamContentRef.current || streamContent);
      if (!contentToConvert || contentToConvert.trim().length === 0) {
        alert('No content available to download');
        setDocxGenerating(false);
        return;
      }

      const programmeName = qualification?.name || responseJson?.qualification_or_module_code || 'LEARNING PROGRAMME';
      const moduleName = module?.title || module?.description || responseJson?.document_title || `Module ${module?.code || '1'}`;
      await downloadDocx({
        programmeName: programmeName.toUpperCase(),
        moduleName,
        contentHtml: contentToConvert
      });
      setDocxGenerating(false);
    } catch (error) {
      alert(`Failed to download Word document: ${error.message}`);
      setDocxGenerating(false);
    }
  }, [editorInstance, streamContentRef, streamContent, qualification, module, responseJson]);

  const handleClosePreview = useCallback(() => {
    setHtmlPreviewContent(null);
    setHtmlPreviewOpen(false);
    setPreviewLoading(false);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      setSaveError(null);
      
      // Get content from CKEditor if available, otherwise use stream content
      // Use ref for content to avoid memory issues, fallback to state
      const contentToSave = editorInstance ? editorInstance.getData() : (streamContentRef.current || streamContent);
      
      if (!contentToSave || contentToSave.trim().length === 0) {
        setSaveError('No content available to save');
        setSaving(false);
        return;
      }

      if (!runId) {
        setSaveError('Run ID is required to save');
        setSaving(false);
        return;
      }

      if (!qualification?.id) {
        setSaveError('Qualification ID is required to save');
        setSaving(false);
        return;
      }

      // Extract module name for title
      const moduleName = module?.title || module?.description || responseJson?.document_title || `Module ${module?.code || '1'}`;

      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const saveResponse = await fetch('/api/learning/save-document', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          run_id: runId,
          html_content: contentToSave,
          title: moduleName,
          qualification_id: qualification.id
        })
      });

      if (!saveResponse.ok) {
        const errorData = await saveResponse.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to save: ${saveResponse.statusText}`);
      }

      setSaveSuccess(true);
      setSaveError(null);
      hasUnsavedEditsRef.current = false;

      // Also persist to BOK so "View in editor" and "View PDF" work (they read from saved-document)
      if (taskCode) {
        try {
          const bokRes = await fetch(`/api/qualifications/${qualification.id}/learning-content/save-build-content`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              subtask_code: taskCode,
              title: moduleName,
              html_content: contentToSave
            })
          });
          if (bokRes.ok && onSaved) onSaved(taskCode);
          else if (bokRes.status === 400) {
            const data = await bokRes.json().catch(() => ({}));
            if (data.code === 'MISSING_END_SECTIONS') {
              setSaveError(data.message || 'Document saved. View PDF / View in editor require References, Glossary, and Abbreviations.');
            }
          }
        } catch (_) {}
      }
    } catch (error) {
      setSaveError(error.message || 'Failed to save document');
      setSaveSuccess(false);
    } finally {
      setSaving(false);
    }
  }, [editorInstance, streamContentRef, streamContent, runId, qualification, module, responseJson, taskCode, onSaved]);

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (htmlPreviewOpen) {
          setHtmlPreviewOpen(false);
          return;
        }
        handleClose();
      }}
      maxWidth="lg"
      fullWidth
      disablePortal={false}
      disableEnforceFocus={false}
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          zIndex: 1300 // Ensure modal appears above other elements in debug mode
        }
      }}
      sx={{
        zIndex: 1300 // Ensure dialog container has proper z-index
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography component="span" variant="h6">
            Generating Learning Content
          </Typography>
          <IconButton onClick={handleClose} size="small" type="button">
            <CloseIcon />
          </IconButton>
        </Stack>
        {runId && (
          <Typography variant="caption" color="text.secondary" component="div">
            Run ID: {runId}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Single consolidated Generation status at top */}
          <Box sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Generation status
              </Typography>
              {isGenerating && (
                <Chip label="Generating..." color="primary" size="small" icon={<InfoIcon />} />
              )}
              {isComplete && (
                <Chip label="Complete" color="success" size="small" icon={<CheckCircleIcon />} />
              )}
              {error && (
                <Chip label="Error" color="error" size="small" icon={<ErrorIcon />} />
              )}
            </Stack>
            {isGenerating && (
              <LinearProgress
                sx={{ mb: 1 }}
                variant="determinate"
                value={Math.min(100, (computedTotalWords / targetWordMin) * 100)}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              {error
                ? `Error: ${error.message || error}`
                : `Words: ${computedTotalWords.toLocaleString()} / ${targetWordMin.toLocaleString()}-${targetWordMax.toLocaleString()}`
              }
            </Typography>
          </Box>

          <Divider />

          {doneMissingEndSections && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={generatingEndSections}
                  onClick={handleGenerateEndSections}
                >
                  {generatingEndSections ? 'Generating…' : 'Generate References, Glossary & Abbreviations'}
                </Button>
              }
            >
              <Typography variant="body2">
                References, Glossary, or Abbreviations are missing. Generate them now so you can save.
                {generateEndSectionsError && ` ${generateEndSectionsError}`}
              </Typography>
            </Alert>
          )}

          {/* Live Streaming Document Editor - Single CKEditor that updates with stream deltas */}
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Live Document Stream
              </Typography>
              {progressSnapshots.length > 0 && (
                <IconButton
                  size="small"
                  onClick={() => setProgressPopupOpen(true)}
                  sx={{ ml: 'auto' }}
                  title="View Progress Snapshots"
                >
                  <InfoIcon color="primary" />
                </IconButton>
              )}
              {isGenerating && (
                <Chip label="Streaming..." size="small" color="info" />
              )}
            </Stack>
            <Box
              className="learner-reading-host"
              sx={{ minHeight: 500, maxHeight: 700, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: 'grey.50' }}
            >
              <Box sx={{ 
                '& .editor-container': {
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                },
                '& .editor-container__menu-bar': {
                  borderBottom: '1px solid #ddd',
                  minHeight: '40px'
                },
                '& .editor-container__toolbar': {
                  borderBottom: '1px solid #ddd',
                  minHeight: '40px'
                },
                '& .editor-container__editor-wrapper': {
                  flex: 1,
                  overflow: 'auto'
                },
                '& .editor-container__editor': {
                  minHeight: '400px'
                }
              }}>
                <div className="editor-container editor-container_document-editor" ref={editorContainerRef}>
                  <div className="editor-container__menu-bar" ref={editorMenuBarRef}></div>
                  <div className="editor-container__toolbar" ref={editorToolbarRef}></div>
                  <div className="editor-container__editor-wrapper">
                    <div className="editor-container__editor">
                      {isEditorLayoutReady && decoupledEditorLoaded && DecoupledEditor && (
                        <CKEditor
                          editor={DecoupledEditor}
                          key="main-editor" // Stable key to prevent recreation
                          // CRITICAL: Don't use data prop - it causes resets
                          // All content will be set via setData() in useEffect
                          config={ckEditorFullConfig}
                          disabled={false} // Ensure editor is always enabled
                          onReady={(editor) => {
                            configureCKEditorUpload(editor);
                            setEditorInstance(editor);

                            // Attach toolbar and menu bar for DecoupledEditor
                            if (editorToolbarRef.current) {
                              // Clear existing toolbar first
                              editorToolbarRef.current.innerHTML = '';
                              editorToolbarRef.current.appendChild(editor.ui.view.toolbar.element);
                            }
                            if (editorMenuBarRef.current) {
                              // Clear existing menubar first
                              editorMenuBarRef.current.innerHTML = '';
                              editorMenuBarRef.current.appendChild(editor.ui.view.menuBarView.element);
                            }
                            
                            // CRITICAL: Set initial content from ref/state
                            // After this, all updates will be done via setData() in useEffect
                            const contentToSet = streamContentRef.current || streamContent || '';
                            
                            if (contentToSet && contentToSet.length > 0) {
                              // Set flag to indicate this is a programmatic update
                              isProgrammaticUpdateRef.current = true;
                              editor.setData(contentToSet);
                              console.log('[LearningProgressModal] ✅ Initial content set on editor ready:', contentToSet.length, 'chars');
                              // Update refs to track what we set
                              lastEditorContentRef.current = contentToSet;
                              lastStreamContentRef.current = contentToSet;
                              maxContentRef.current = contentToSet;
                              maxContentLengthRef.current = contentToSet.length;
                              // Reset flag after a short delay
                              setTimeout(() => {
                                isProgrammaticUpdateRef.current = false;
                              }, 50);
                            } else {
                              console.log('[LearningProgressModal] ⚠️ No content to set on editor ready');
                            }
                          }}
                          onChange={(event, editor) => {
                            const data = editor.getData();

                            // CRITICAL: Don't update streamContent if this change came from programmatic setData()
                            // This prevents infinite loops where setData() triggers onChange which updates state
                            // which triggers useEffect which calls setData() again
                            if (isProgrammaticUpdateRef.current) {
                              // This is a programmatic update, don't update state
                              return;
                            }

                            // CRITICAL: During generation, don't let user edits interfere with stream updates
                            // The backend is sending full accumulated HTML - we must preserve it
                            if (status === 'generating') {
                              // During generation, ignore user edits to maintain stream
                              // User can edit after generation completes
                              return;
                            }

                            // CRITICAL: Don't update if content hasn't actually changed
                            // This prevents unnecessary state updates that trigger re-renders
                            if (data === streamContentRef.current || data === streamContent) {
                              return; // Content hasn't changed, skip update
                            }

                            // Mark that this is a user edit to prevent infinite loop
                            isUserEditRef.current = true;
                            // Revert saved state so Save button shows unsaved and we can warn on close
                            setSaveSuccess(false);
                            hasUnsavedEditsRef.current = true;

                            // Update streamContent when user edits (only if content actually changed)
                            setStreamContent(data);
                            
                            // Also update ref immediately to track current content
                            streamContentRef.current = data;
                            lastEditorContentRef.current = data;
                            
                            // Reset flag after a short delay to allow state update
                            setTimeout(() => {
                              isUserEditRef.current = false;
                            }, 100);
                          }}
                          onAfterDestroy={() => {
                            // Clean up toolbar and menu bar
                            if (editorToolbarRef.current) {
                              Array.from(editorToolbarRef.current.children).forEach(child => child.remove());
                            }
                            if (editorMenuBarRef.current) {
                              Array.from(editorMenuBarRef.current.children).forEach(child => child.remove());
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </Box>
            </Box>
          </Box>

          {/* Debug Logs Section */}
          <Accordion 
            expanded={expandedDebug} 
            onChange={(e, isExpanded) => setExpandedDebug(isExpanded)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Debug Logs
                </Typography>
                <Chip label={debugLogs.length} size="small" variant="outlined" />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {debugLogs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No debug logs yet...
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {debugLogs.map((log, index) => (
                      <Paper 
                        key={index}
                        variant="outlined"
                        sx={{ 
                          p: 1.5,
                          bgcolor: log.level === 'error' ? 'error.light' : 
                                  log.level === 'warning' ? 'warning.light' : 
                                  'grey.50'
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <LogLevelIcon level={log.level} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {log.message}
                            </Typography>
                            {log.timestamp && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {new Date(log.timestamp).toLocaleTimeString()}
                              </Typography>
                            )}
                            {log.details && (
                              <Box sx={{ mt: 0.5 }}>
                                {log.details.fullText ? (
                                  <Paper 
                                    variant="outlined" 
                                    sx={{ 
                                      p: 1.5,
                                      bgcolor: 'grey.50',
                                      maxHeight: 300,
                                      overflowY: 'auto',
                                      fontSize: '0.75rem',
                                      fontFamily: 'monospace',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {log.details.fullText}
                                  </Paper>
                                ) : log.details.preview ? (
                                  <Paper 
                                    variant="outlined" 
                                    sx={{ 
                                      p: 1.5,
                                      bgcolor: 'grey.50',
                                      maxHeight: 200,
                                      overflowY: 'auto',
                                      fontSize: '0.75rem',
                                      fontFamily: 'monospace',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {log.details.preview}
                                    {log.details.accumulatedLength && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Total length: {log.details.accumulatedLength} characters
                                      </Typography>
                                    )}
                                  </Paper>
                                ) : (
                                  <Typography 
                                    variant="caption" 
                                    component="pre" 
                                    sx={{ 
                                      fontSize: '0.7rem',
                                      overflowX: 'auto',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {JSON.stringify(log.details, null, 2)}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                    <div ref={logsEndRef} />
                  </Stack>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>

      <DialogActions>
        {/* Continue Generation button - show when target not reached and generation stopped */}
        {isComplete && computedTotalWords < targetWordMin && streamContent && streamContent.length > 0 && (
          <Button 
            onClick={() => {
              // Close modal and trigger continue generation in parent
              handleClose();
              if (onComplete) {
                // Pass empty array to continue generation (word-based, no pages)
                setTimeout(() => onComplete([]), 100);
              }
            }}
            variant="outlined" 
            color="primary"
            disabled={isGenerating}
          >
            Continue Generation ({computedTotalWords.toLocaleString()}/{targetWordMin.toLocaleString()} words)
          </Button>
        )}
        {/* Preview button - only show when target pages reached */}
        {isComplete && computedTotalWords >= targetWordMin && streamContent && (
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePreviewHTML(e);
            }} 
            variant="contained" 
            startIcon={previewLoading ? <CircularProgress size={16} /> : <VisibilityIcon />}
            color="primary"
            disabled={previewLoading}
          >
            {previewLoading ? 'Loading Preview...' : 'Preview'}
          </Button>
        )}
        {/* View PDF - only when generation is complete and word target reached */}
        {isComplete && computedTotalWords >= targetWordMin && streamContent && (editorInstance || streamContent.length > 0) && (
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleGeneratePDF();
            }} 
            variant="contained" 
            startIcon={pdfGenerating ? <CircularProgress size={16} /> : <DownloadIcon />}
            color="primary"
            disabled={pdfGenerating || (!editorInstance && !streamContent) || (editorInstance && !editorInstance.getData() && !streamContent)}
          >
            {pdfGenerating ? 'Generating PDF...' : 'View PDF'}
          </Button>
        )}
        {isComplete && computedTotalWords >= targetWordMin && streamContent && (editorInstance || streamContent.length > 0) && (
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDownloadWord();
            }}
            variant="contained"
            startIcon={docxGenerating ? <CircularProgress size={16} /> : <DescriptionIcon />}
            color="primary"
            disabled={docxGenerating || (!editorInstance && !streamContent) || (editorInstance && !editorInstance.getData() && !streamContent)}
          >
            {docxGenerating ? 'Downloading Word...' : 'Download Word'}
          </Button>
        )}
        {/* Save - only when generation is complete and word target reached */}
        {isComplete && computedTotalWords >= targetWordMin && streamContent && (editorInstance || streamContent.length > 0) && (
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }} 
            variant="contained" 
            startIcon={saving ? <CircularProgress size={16} /> : (saveSuccess ? <CheckCircleIcon /> : <SaveIcon />)}
            color={saveSuccess ? "success" : "primary"}
            disabled={saving || !runId || !qualification?.id}
            sx={{ 
              ...(saveSuccess && { 
                bgcolor: 'success.main',
                '&:hover': { bgcolor: 'success.dark' }
              })
            }}
          >
            {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save'}
          </Button>
        )}
        {/* Show save error if any */}
        {saveError && (
          <Typography variant="caption" color="error" sx={{ ml: 1 }}>
            {saveError}
          </Typography>
        )}
        {/* Cancel button - only while actively generating; hide when document is complete */}
        {status === 'generating' && onCancel && (
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Only call onCancel to stop the stream - don't close modal
              // User can close via X button to view generated content
              if (onCancel) {
                onCancel();
              }
            }} 
            variant="contained"
            color="error"
          >
            Cancel Generation
          </Button>
        )}
        
        {/* Close button - always enabled so user can close */}
        <Button 
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClose();
          }} 
          variant={isComplete ? "outlined" : "outlined"}
        >
          {isComplete ? 'Close' : error ? 'Close' : 'Close'}
        </Button>
      </DialogActions>

      {/* HTML Preview Modal */}
      <Dialog
        open={htmlPreviewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography component="span" variant="h6">Document Preview</Typography>
            <IconButton onClick={handleClosePreview} size="small" type="button">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ width: '100%', height: 'calc(90vh - 120px)', display: 'flex', flexDirection: 'column' }}>
            {htmlPreviewContent ? (
              <Box 
                sx={{ 
                  flex: 1, 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  overflow: 'auto',
                  p: 2,
                  backgroundColor: '#fff'
                }}
                ref={(el) => {
                  // Handle image errors in preview to prevent infinite loading
                  if (el && htmlPreviewContent) {
                    // Use setTimeout to ensure DOM is ready
                    setTimeout(() => {
                      const images = el.querySelectorAll('img');
                      images.forEach(img => {
                        // Set timeout for image loading (3 seconds)
                        const timeout = setTimeout(() => {
                          if (!img.complete || img.naturalHeight === 0) {

                            img.style.display = 'none';
                            const placeholder = document.createTextNode('[Image unavailable]');
                            if (img.parentNode) {
                              img.parentNode.insertBefore(placeholder, img);
                            }
                          }
                        }, 3000);
                        
                        img.onload = () => {
                          clearTimeout(timeout);
                        };
                        
                        img.onerror = function() {
                          clearTimeout(timeout);

                          this.style.display = 'none';
                          const placeholder = document.createTextNode('[Image unavailable]');
                          if (this.parentNode) {
                            this.parentNode.insertBefore(placeholder, this);
                          }
                        };
                      });
                    }, 100);
                  }
                }}
                dangerouslySetInnerHTML={{ __html: htmlPreviewContent }}
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 2 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Loading preview...
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleGeneratePDF}
            variant="contained"
            startIcon={pdfGenerating ? <CircularProgress size={16} /> : <DownloadIcon />}
            color="primary"
            disabled={pdfGenerating || (!editorInstance && !streamContent) || (editorInstance && !editorInstance.getData() && !streamContent)}
          >
            {pdfGenerating ? 'Generating PDF...' : 'View PDF'}
          </Button>
          <Button
            onClick={handleDownloadWord}
            variant="contained"
            startIcon={docxGenerating ? <CircularProgress size={16} /> : <DescriptionIcon />}
            color="primary"
            disabled={docxGenerating || (!editorInstance && !streamContent) || (editorInstance && !editorInstance.getData() && !streamContent)}
          >
            {docxGenerating ? 'Downloading Word...' : 'Download Word'}
          </Button>
          <Button onClick={handleClosePreview} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Progress Snapshots Popup */}
      <Popover
        open={progressPopupOpen}
        onClose={() => setProgressPopupOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 400, maxWidth: 600, maxHeight: 500, overflow: 'auto' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Progress Snapshots</Typography>
            <IconButton size="small" onClick={() => setProgressPopupOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          {progressSnapshots.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No progress snapshots available yet.
            </Typography>
          ) : (
            <List dense>
              {progressSnapshots.map((snapshot, index) => (
                <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" fontWeight="bold">
                        Page {snapshot.pageNumber} - {new Date(snapshot.timestamp).toLocaleTimeString()}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" component="div">
                          <strong>Total Words:</strong> {snapshot.snapshot?.totalWordsSoFar?.toLocaleString() || 0} / {snapshot.snapshot?.targetWordRange?.min?.toLocaleString() || 0}-{snapshot.snapshot?.targetWordRange?.max?.toLocaleString() || 0}
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                          <strong>Last Generated Page:</strong> {snapshot.snapshot?.lastGeneratedPage || 0}
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                          <strong>Pages Done:</strong> {snapshot.snapshot?.pages?.filter(p => p.status === 'done').length || 0} / {snapshot.snapshot?.pages?.length || 0}
                        </Typography>
                        {snapshot.snapshot?.pages && snapshot.snapshot.pages.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" fontWeight="bold">Pages:</Typography>
                            <List dense sx={{ pl: 1 }}>
                              {snapshot.snapshot.pages.map((page, idx) => (
                                <ListItem key={idx} sx={{ py: 0.25 }}>
                                  <ListItemText
                                    primary={
                                      <Typography variant="caption">
                                        Page {page.page}: {page.title || 'Untitled'} - {page.status} ({page.wordCount || 0} words)
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Popover>

      {/* Citation Guide Popover */}
      <Popover
        open={citationGuidePopoverOpen}
        anchorEl={citationGuidePopoverAnchor}
        onClose={() => {
          setCitationGuidePopoverOpen(false);
          setCitationGuidePopoverAnchor(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            maxWidth: 800,
            maxHeight: '80vh',
            overflow: 'auto'
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              {harvardCitationGuide.title}
            </Typography>
            <IconButton size="small" onClick={() => {
              setCitationGuidePopoverOpen(false);
              setCitationGuidePopoverAnchor(null);
            }}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 3 }} />
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            {harvardCitationGuide.introduction}
          </Typography>

          {harvardCitationGuide.sections.map((section, index) => (
            <Box key={index} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                {section.title}
              </Typography>
              
              {section.content && (
                <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                  {section.content}
                </Typography>
              )}

              {section.subsections && section.subsections.map((subsection, subIndex) => (
                <Box key={subIndex} sx={{ mb: 2, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    {subsection.title}
                  </Typography>
                  {subsection.example && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" component="div" sx={{ fontStyle: 'italic', whiteSpace: 'pre-line' }}>
                        {subsection.example}
                      </Typography>
                    </Paper>
                  )}
                  {subsection.examples && (
                    <Box sx={{ mb: 1 }}>
                      {subsection.examples.map((example, exIndex) => (
                        <Paper key={exIndex} variant="outlined" sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}>
                          <Typography variant="body2" component="div" sx={{ fontStyle: 'italic' }}>
                            {example}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  )}
                  {subsection.note && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Note: {subsection.note}
                    </Typography>
                  )}
                </Box>
              ))}

              {section.examples && (
                <Box sx={{ mt: 2 }}>
                  {Array.isArray(section.examples) && section.examples.map((example, exIndex) => {
                    if (typeof example === 'string') {
                      return (
                        <Typography key={exIndex} variant="body2" sx={{ mb: 1, pl: 2, fontFamily: 'monospace', fontSize: '0.9em' }}>
                          {example}
                        </Typography>
                      );
                    } else if (typeof example === 'object' && example.type) {
                      return (
                        <Box key={exIndex} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                            {example.type}
                          </Typography>
                          {example.inText && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>In-text:</strong> <span style={{ fontStyle: 'italic' }}>{example.inText}</span>
                            </Typography>
                          )}
                          {example.reference && (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                              <strong>Reference:</strong> {example.reference}
                            </Typography>
                          )}
                        </Box>
                      );
                    }
                    return null;
                  })}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Popover>
    </Dialog>
  );
}

